import { useState, useEffect } from "react";
import { ArrowLeft, Bot, Calculator, FileText, Truck, TrendingUp, DollarSign, MessageCircle, Check, type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Textarea } from "@/components/ui/Textarea";
import { Switch } from "@/components/ui/Switch";
import { useAgentStore } from "@/store/AgentStore";
import { mockAgents, contextTemplates, responseStyleOptions } from "@/constants/agents";
import { categoryLabels, defaultAgentPreference } from "@/types/agent";
import { cn } from "@/lib/utils";

const iconMap: Record<string, LucideIcon> = {
  Bot,
  Calculator,
  FileText,
  Truck,
  TrendingUp,
  DollarSign,
  MessageCircle,
};

function getIconComponent(iconName: string): LucideIcon {
  return iconMap[iconName] || Bot;
}

interface AgentSettingsProps {
  agentId: string;
  onBack?: () => void;
}

export function AgentSettings({ agentId, onBack }: AgentSettingsProps) {
  const { getAgentPreference, setAgentPreference } = useAgentStore();

  const agent = mockAgents.find((a) => a.id === agentId);
  const existingPreference = agentId ? getAgentPreference(agentId) : defaultAgentPreference;

  const [context, setContext] = useState(existingPreference.context);
  const [responseStyle, setResponseStyle] = useState(existingPreference.responseStyle);
  const [autoSave, setAutoSave] = useState(existingPreference.autoSave);
  const [notifications, setNotifications] = useState(existingPreference.notifications);
  const [allowDataOptimization, setAllowDataOptimization] = useState(existingPreference.allowDataOptimization);

  useEffect(() => {
    if (existingPreference) {
      setContext(existingPreference.context);
      setResponseStyle(existingPreference.responseStyle);
      setAutoSave(existingPreference.autoSave);
      setNotifications(existingPreference.notifications);
      setAllowDataOptimization(existingPreference.allowDataOptimization);
    }
  }, [existingPreference]);

  if (!agent) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">智能体不存在</p>
      </div>
    );
  }

  const IconComponent = getIconComponent(agent.icon);

  const handleSave = () => {
    if (agentId) {
      setAgentPreference(agentId, {
        context,
        responseStyle,
        autoSave,
        notifications,
        allowDataOptimization,
      });
    }
    onBack?.();
  };

  const handleReset = () => {
    setContext(defaultAgentPreference.context);
    setResponseStyle(defaultAgentPreference.responseStyle);
    setAutoSave(defaultAgentPreference.autoSave);
    setNotifications(defaultAgentPreference.notifications);
    setAllowDataOptimization(defaultAgentPreference.allowDataOptimization);
  };

  const applyTemplate = (content: string) => {
    setContext(content);
  };

  return (
    <div className="h-screen bg-background overflow-y-auto">
      <div className="container mx-auto px-4 py-8 max-w-5xl min-h-full pb-24">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold">智能体设置</h1>
        </div>

        <div className="flex items-center gap-4 mb-8 p-6 bg-card rounded-xl border">
          <div className={`w-16 h-16 rounded-xl ${agent.iconBgColor} flex items-center justify-center`}>
            <IconComponent className={`w-8 h-8 ${agent.iconColor}`} />
          </div>
          <div>
            <h2 className="text-xl font-semibold">{agent.name}</h2>
            <span className="inline-flex mt-1 px-2 py-0.5 text-xs font-medium rounded-md bg-gray-100 text-gray-600">
              {categoryLabels[agent.category]}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-6">
            <div className="bg-card rounded-xl border p-6">
              <h3 className="text-lg font-semibold mb-4">上下文设置</h3>
              <p className="text-sm text-muted-foreground mb-4">
                设置智能体的系统上下文，让智能体更好地理解您的业务场景和需求。
              </p>

              <Textarea
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder="例如：我是一家物流公司的财务人员，需要处理每月的增值税申报..."
                className="min-h-[160px] mb-4"
              />

              <div>
                <p className="text-sm font-medium mb-2">快捷模板</p>
                <div className="flex flex-wrap gap-2">
                  {contextTemplates.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => applyTemplate(template.content)}
                      className="px-3 py-1.5 text-xs font-medium rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                    >
                      {template.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-card rounded-xl border p-6">
              <h3 className="text-lg font-semibold mb-4">响应风格</h3>
              <p className="text-sm text-muted-foreground mb-4">
                自定义智能体的响应方式。
              </p>

              <div className="space-y-2">
                {responseStyleOptions.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setResponseStyle(option.id)}
                    className={cn(
                      "w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors",
                      responseStyle === option.id
                        ? "bg-blue-50 border-blue-200"
                        : "hover:bg-gray-50"
                    )}
                  >
                    <div
                      className={cn(
                        "w-4 h-4 rounded-full border flex items-center justify-center",
                        responseStyle === option.id
                          ? "border-blue-500 bg-blue-500"
                          : "border-gray-300"
                      )}
                    >
                      {responseStyle === option.id && (
                        <div className="w-1.5 h-1.5 rounded-full bg-white" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{option.label}</p>
                      <p className="text-sm text-muted-foreground">{option.description}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-card rounded-xl border p-6">
              <h3 className="text-lg font-semibold mb-4">其他设置</h3>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">自动保存对话</p>
                    <p className="text-sm text-muted-foreground">每次对话结束后自动保存到历史记录</p>
                  </div>
                  <Switch checked={autoSave} onCheckedChange={setAutoSave} />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">启用通知</p>
                    <p className="text-sm text-muted-foreground">当智能体完成任务时发送通知</p>
                  </div>
                  <Switch checked={notifications} onCheckedChange={setNotifications} />
                </div>
              </div>
            </div>

            <div className="bg-card rounded-xl border p-6">
              <h3 className="text-lg font-semibold mb-4">数据隐私</h3>

              <div className="flex items-start gap-3">
                <button
                  onClick={() => setAllowDataOptimization(!allowDataOptimization)}
                  className={cn(
                    "w-5 h-5 rounded flex items-center justify-center transition-colors flex-shrink-0 mt-0.5",
                    allowDataOptimization
                      ? "bg-blue-500 text-white"
                      : "border border-gray-300"
                  )}
                >
                  {allowDataOptimization && <Check className="w-3 h-3" />}
                </button>
                <div>
                  <p className="font-medium">允许使用对话数据进行模型优化</p>
                  <p className="text-sm text-muted-foreground">
                    帮助我们改进智能体的回答质量
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-3 mt-8 pt-6 border-t sticky bottom-0 bg-background py-4 -mx-4 px-4 sm:static sm:mx-0 sm:px-0 sm:py-0">
          <Button variant="outline" onClick={onBack} className="w-full sm:w-auto">
            取消
          </Button>
          <Button variant="outline" onClick={handleReset} className="w-full sm:w-auto">
            重置
          </Button>
          <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto">
            保存设置
          </Button>
        </div>
      </div>
    </div>
  );
}

export default AgentSettings;
